import { createContext, useContext, useLayoutEffect, useMemo, useState, type ReactNode } from 'react'

export type ThemeMode = 'system' | 'light' | 'dark'
export type ResolvedTheme = Exclude<ThemeMode, 'system'>

export const THEME_STORAGE_KEY = 'myserbisyo.theme.v1'

function storedTheme(): ThemeMode {
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY)
    return value === 'light' || value === 'dark' || value === 'system' ? value : 'system'
  } catch {
    return 'system'
  }
}

function resolveTheme(mode: ThemeMode): ResolvedTheme {
  if (mode !== 'system') return mode
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function applyTheme(theme: ResolvedTheme) {
  document.documentElement.dataset.theme = theme
  document.documentElement.style.colorScheme = theme
}

/** Apply the saved preference before React renders so dark-mode users do not see a light flash. */
export function initializeTheme() {
  applyTheme(resolveTheme(storedTheme()))
}

type ThemeContextValue = {
  mode: ThemeMode
  resolvedTheme: ResolvedTheme
  setMode: (mode: ThemeMode) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(storedTheme)
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolveTheme(mode))

  // The layout effect keeps the document state ahead of route content when a person switches modes.
  useLayoutEffect(() => {
    const update = () => {
      const next = resolveTheme(mode)
      setResolvedTheme(next)
      applyTheme(next)
    }
    update()
    if (mode !== 'system') return
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [mode])

  const setMode = (next: ThemeMode) => {
    setModeState(next)
    try { window.localStorage.setItem(THEME_STORAGE_KEY, next) } catch { /* private browsing can deny storage */ }
  }

  const value = useMemo(() => ({ mode, resolvedTheme, setMode }), [mode, resolvedTheme])
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const value = useContext(ThemeContext)
  if (!value) throw new Error('useTheme must be used inside ThemeProvider')
  return value
}

/** Keeps the tiny account-area theme control consistent wherever it is surfaced. */
export function ThemeModeSelect({ className = '' }: { className?: string }) {
  const { mode, setMode } = useTheme()
  return (
    <label className={`flex min-w-0 items-center gap-2 ${className}`}>
      <span className="text-[11.5px] font-medium text-token-muted">Theme</span>
      <select
        aria-label="Theme"
        value={mode}
        onChange={(event) => setMode(event.target.value as ThemeMode)}
        className="min-w-0 flex-1 appearance-none rounded-[8px] border border-token bg-token-surface px-2 py-1.5 text-[12px] font-semibold text-token-primary outline-none transition-colors hover:bg-token-hover focus:border-token-strong [@media(pointer:coarse)]:min-h-[44px]"
      >
        <option value="system">System</option>
        <option value="light">Light</option>
        <option value="dark">Dark</option>
      </select>
    </label>
  )
}
